import { useCallback, useEffect, useRef, useState } from "react";
import type { Column } from "@tanstack/react-table";
import { cn } from "@/lib/utils";

// Weight for columns whose meta.width is absent or not a %/px length; they
// get a mid-sized share the user can adjust by dragging.
const FALLBACK_COL_WEIGHT = 150;

// Legacy meta.width declarations are heterogeneous (numbers, "56px", "30%");
// normalize to comparable pixels for the rescale math.
function parseMetaWidth(
  width: string | number | undefined,
  containerW: number
): number {
  if (typeof width === "number") return width;
  if (typeof width === "string") {
    const value = parseFloat(width);
    if (Number.isFinite(value)) {
      if (width.endsWith("%")) return (value / 100) * containerW;
      if (width.endsWith("px")) return value;
    }
  }
  return FALLBACK_COL_WEIGHT;
}

// Rescale stored px widths so data columns fill the container minus the
// fixed utility columns. Not calc(): browsers ignore calc() widths on cells
// in fixed table layout. Returns null while nothing has been user-resized.
export function computeScaledWidths<Data extends object>(
  visibleColumns: Column<Data, unknown>[],
  storedWidths: Record<string, number>,
  containerW: number,
  fixedColumnIds: ReadonlySet<string>
): Record<string, number> | null {
  if (containerW <= 0 || Object.keys(storedWidths).length === 0) return null;
  let fixedPx = 0;
  const weights: Array<[string, number]> = [];
  for (const col of visibleColumns) {
    const metaWidth = col.columnDef.meta?.width;
    if (fixedColumnIds.has(col.id)) {
      fixedPx += parseMetaWidth(metaWidth, containerW);
    } else {
      weights.push([
        col.id,
        storedWidths[col.id] ?? parseMetaWidth(metaWidth, containerW),
      ]);
    }
  }
  const total = weights.reduce((sum, [, weight]) => sum + weight, 0);
  const avail = containerW - fixedPx;
  if (total <= 0 || avail <= 0) return null;
  return Object.fromEntries(
    weights.map(([id, weight]) => [id, (weight / total) * avail])
  );
}

export type ColumnDragState = {
  col: string;
  next?: string;
  startX: number;
  // Rendered widths of all data columns, snapshotted at drag start.
  widths: Record<string, number>;
};

// Dragging a column's right border resizes that column alone, down to `min`:
// every column to its right keeps its own width and simply moves along with
// the drag. Nothing here stops the table shrinking below its container --
// fillLastColumn takes care of that, so a drag is never blocked by the width
// of columns the user is not touching.
export function pushWidth({
  col,
  widths,
  deltaX,
  min,
}: {
  col: string;
  widths: Record<string, number>;
  deltaX: number;
  min: number;
}): Record<string, number> {
  const startW = widths[col];
  if (startW === undefined) return {};
  return { [col]: Math.max(min, startW + deltaX) };
}

// Rendered widths: the last column absorbs whatever space the others leave, so
// the table always spans at least its container. Without that the table's
// right edge would detach from the wrapper and the pinned utility column would
// ride left inside a full-height gap (DEV-2240). Over-full stays over-full and
// overflows into horizontal scroll.
export function fillLastColumn(nominal: number[], availW: number): number[] {
  if (nominal.length === 0 || availW <= 0) return nominal;
  const slack = availW - nominal.reduce((a, b) => a + b, 0);
  if (slack <= 0) return nominal;
  return nominal.map((w, i) => (i === nominal.length - 1 ? w + slack : w));
}

// Zero-sum trade with the right neighbour: the boundary follows the cursor and
// the table's total width never changes, so the table always ends a drag
// exactly as wide as it started (DEV-2240 -- a shrinking total used to detach
// the table's right edge from the wrapper and drag the pinned utility column
// left with it). A column that starts below `min` can grow but not shrink.
export function tradeWidths({
  col,
  next,
  widths,
  deltaX,
  min,
}: {
  col: string;
  next: string;
  widths: Record<string, number>;
  deltaX: number;
  min: number;
}): Record<string, number> {
  const startW = widths[col];
  const startNextW = widths[next];
  if (startW === undefined || startNextW === undefined) return {};
  const delta = Math.max(
    Math.min(min, startW) - startW,
    Math.min(startNextW - Math.min(min, startNextW), deltaX)
  );
  return { [col]: startW + delta, [next]: startNextW - delta };
}

// A press has to travel this far before it counts as a drag. Below it the
// press stays a plain click: no overlay, no width change. That is what lets the
// handle's own dblclick fire -- the overlay is fixed inset-0, so mounting it on
// mousedown would take the mouseup, and the browser would never pair the two
// clicks into a double-click.
const DRAG_SLOP_PX = 3;

// Shared drag lifecycle for resizable table columns: document-level mouse
// tracking, drag bookkeeping, and text-selection suppression. Width semantics
// stay with the caller via computeUpdates: pushWidth in the table grids,
// tradeWidths in DataTable, whose layout must stay exactly container-wide.
export function useColumnDrag({
  enabled,
  computeUpdates,
  onChange,
  onFirstMove,
  onDragEnd,
}: {
  enabled: boolean;
  computeUpdates: (
    drag: ColumnDragState,
    deltaX: number
  ) => Record<string, number>;
  onChange: (updates: Record<string, number>) => void;
  // Fired once the press turns into a real drag, before the first update --
  // where the caller commits the width snapshot it took on mousedown.
  onFirstMove?: (drag: ColumnDragState) => void;
  onDragEnd?: () => void;
}) {
  const [resizingCol, setResizingCol] = useState<string | null>(null);
  const dragRef = useRef<ColumnDragState | null>(null);
  const movedRef = useRef(false);
  const computeRef = useRef(computeUpdates);
  computeRef.current = computeUpdates;
  const changeRef = useRef(onChange);
  changeRef.current = onChange;
  const firstMoveRef = useRef(onFirstMove);
  firstMoveRef.current = onFirstMove;
  const endRef = useRef(onDragEnd);
  endRef.current = onDragEnd;

  useEffect(() => {
    if (!enabled) return;
    const cancel = () => {
      dragRef.current = null;
      movedRef.current = false;
      document.body.style.userSelect = "";
      setResizingCol(null);
    };
    const onMove = (e: MouseEvent) => {
      const drag = dragRef.current;
      if (!drag) return;
      const deltaX = e.clientX - drag.startX;
      if (!movedRef.current) {
        if (Math.abs(deltaX) < DRAG_SLOP_PX) return;
        movedRef.current = true;
        document.body.style.userSelect = "none";
        setResizingCol(drag.col);
        firstMoveRef.current?.(drag);
      }
      changeRef.current(computeRef.current(drag, deltaX));
    };
    const onUp = () => {
      if (!dragRef.current) return;
      const moved = movedRef.current;
      cancel();
      if (moved) endRef.current?.();
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
    return () => {
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
      // Abort a live drag (unmount or enabled flip): without this the
      // userSelect suppression would stick app-wide forever.
      if (dragRef.current) cancel();
    };
  }, [enabled]);

  const beginDrag = useCallback((drag: ColumnDragState) => {
    dragRef.current = drag;
    movedRef.current = false;
  }, []);

  const isDragging = useCallback(() => dragRef.current !== null, []);

  return { resizingCol, beginDrag, isDragging };
}

// 8px-wide drag target on a column's right border, with a 2px strip that
// highlights on hover. Clicks are swallowed so finishing a drag never triggers
// the header's own onClick.
//
// `inset` keeps the target wholly inside the cell. Use it on the last column:
// a pinned utility column after it paints over the overhang, and with none the
// overhang widens the table past its scroll container.
export function ColumnResizeHandle({
  label,
  isResizing,
  inset = false,
  onDragStart,
  onNarrow,
}: {
  label: string;
  isResizing: boolean;
  inset?: boolean;
  onDragStart: (startX: number) => void;
  onNarrow?: () => void;
}) {
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label={label}
      title={onNarrow ? "Drag to resize · double-click to narrow" : undefined}
      className={cn(
        "absolute top-0 z-20 h-full w-2 cursor-col-resize group/resize",
        inset ? "right-0" : "-right-1"
      )}
      onClick={(e) => e.stopPropagation()}
      onDoubleClick={
        onNarrow
          ? (e) => {
              e.stopPropagation();
              onNarrow();
            }
          : undefined
      }
      onMouseDown={(e) => {
        // mousedown fires for every button; only the left one drags.
        if (e.button !== 0) return;
        e.preventDefault();
        e.stopPropagation();
        onDragStart(e.clientX);
      }}
    >
      <div
        className={cn(
          "absolute inset-y-0 w-0.5 transition-colors",
          inset ? "right-0" : "left-1/2 -translate-x-1/2",
          isResizing
            ? "bg-muted-foreground/60"
            : "group-hover/resize:bg-muted-foreground/40"
        )}
      />
    </div>
  );
}

// Full-viewport overlay that keeps the col-resize cursor uniform during a
// drag; tracking stays on the document listeners.
export function ColumnResizeOverlay({ active }: { active: boolean }) {
  if (!active) return null;
  return <div className="fixed inset-0 z-50 cursor-col-resize select-none" />;
}
